import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_aplicaciones";
const DB_VERSION = 4;
const STORE_APLICACIONES = "aplicaciones";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem` en `GrpcLoteDatabase.ts`: watermark por finca, no uno
 *  global). */
export type GrpcAplicacionItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para las aplicaciones que trae
 *  `AplicacionGrpcService` (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web) — mismo patrón que `GrpcLoteDatabase`: delta real por
 *  `fincaId`, cada finca sincroniza independiente. */
class GrpcAplicacionDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor.
   *
   *  **Versión 4 (2026-10-15): cambió la forma del item.** `AplicacionItem`
   *  sumó `metodo` y `lotes[]`, y acá se guarda el item tal como llega, así que
   *  las aplicaciones cacheadas antes quedaron sin esos campos. El sync por
   *  delta no las vuelve a pedir —no cambiaron en el servidor— y el mapeo
   *  reventaba al leer `lotes` de un item que no lo tiene. Vaciar y rellenar es
   *  la única forma de que los registros viejos traigan los campos nuevos. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_APLICACIONES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 4 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Watermark de la última sincronización exitosa de una finca. `0n` si
   *  todavía no sincronizó nunca (trae el listado completo). */
  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  /** Aplica un delta de una finca: `put` de las aplicaciones vivas, `delete`
   *  de las marcadas `eliminado`, y guarda el nuevo watermark de esa finca. */
  async putDeltaAsync<T extends GrpcAplicacionItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_APLICACIONES, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_APLICACIONES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Todas las aplicaciones guardadas de una finca, vía el índice `fincaId`. */
  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_APLICACIONES, "readonly")
      .objectStore(STORE_APLICACIONES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) una sola aplicación sin tocar el resto — lo llama
   *  el guardado puntual para que los consumidores vean el cambio sin
   *  esperar al próximo delta. */
  async putOneAsync<T extends GrpcAplicacionItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_APLICACIONES, "readwrite");
    tx.objectStore(STORE_APLICACIONES).put(item);
    await txDone(tx);
  }

  /** Borra una aplicación puntual (baja lógica confirmada por el server). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_APLICACIONES, "readwrite");
    tx.objectStore(STORE_APLICACIONES).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de aplicaciones por gRPC-Web (con delta por finca). */
export const grpcAplicacionDatabase = new GrpcAplicacionDatabase();
